import { format } from 'date-fns';
import { AccountRewardsInfo } from '../../../../../../shared/entities/AccountRewardsInfo';
import { InputHandler, useInput } from '../../../../../ui/Input/useInput';
import { DineRewardsManager } from '../../../../../../shared/managers/DineRewards.manager';
import { UserInfo } from '../../../../../../shared/entities/UserInfo';
import { Strings } from '../../../../../../shared/constants/Strings';
import { AccountRegisterInfo } from '../../../../../../shared/entities/AccountRegisterInfo';
import { stringToDate } from '@maverick/utils';

export interface Inputs {
	phoneNumber: InputHandler<string>;
	email: InputHandler<string>;
	newPassword: InputHandler<string>;
	confirmNewPassword: InputHandler<string>;
	firstName: InputHandler<string>;
	lastName: InputHandler<string>;
	zipCode: InputHandler<string>;
	birthDate: InputHandler<string>;
}

export const FavoriteLocations = {
	Default: `Where do you love to dine`,
	Outback: `Outback Steakhouse`,
	Carrabbas: `Carrabba's Italian Grill`,
	Bonefish: `Bonefish Grill`,
	Fleming: `Fleming's Steakhouse & Wine Bar`,
};

export const setMemberProfileInputs = (userData: AccountRewardsInfo, inputs: Inputs) => {
	inputs.phoneNumber.setValue(userData?.phone);
	inputs.email.setValue(userData?.email);
	inputs.newPassword.setValue('');
	inputs.confirmNewPassword.setValue('');
	inputs.firstName.setValue(userData?.firstName);
	inputs.lastName.setValue(userData?.lastName);
	inputs.zipCode.setValue(userData?.postalCode);

	if (userData?.dateOfBirth) {
		const birthdayDate = new Date(userData?.dateOfBirth);
		const formattedDate = format(birthdayDate, 'MM/dd/yyyy');
		inputs.birthDate.setValue(formattedDate);
	}
};

export const createMemberProfileInputs = () => {
	const inputs: Inputs = {
		phoneNumber: useInput('', {
			label: 'Type your phone number here',
			placeholder: '(224) 284-3154',
			mask: '(xxx) xxx-xxxx',
			maxlength: 14,
			disabled: true,
			id: 'phone',
		}),
		email: useInput('', {
			value: 'userData?.email',
			label: 'Enter email',
			placeholder: 'useremail@gmail.com',
			disabled: true,
			id: 'email',
		}),
		newPassword: useInput('', {
			label: 'Enter new password',
			placeholder: 'Type your new password',
			disabled: true,
			id: 'new-password',
		}),
		confirmNewPassword: useInput('', {
			label: 'Confirm new password',
			placeholder: 'Confirm you new password here',
			disabled: true,
			id: 'confirm-new-password',
		}),
		firstName: useInput('', {
			label: 'Type your first name here',
			placeholder: 'User',
			disabled: true,
			id: 'first-name',
		}),
		lastName: useInput('', {
			label: 'Type your last name here',
			placeholder: 'Surname',
			disabled: true,
			id: 'last-name',
		}),
		zipCode: useInput('', {
			label: 'Enter your zip code',
			placeholder: 'Type your zip code',
			maxlength: 5,
			disabled: true,
			id: 'zip-code',
		}),
		birthDate: useInput('', {
			label: 'Enter your birthday',
			placeholder: 'mm/dd/yyyy',
			mask: 'xx/xx/xxxx',
			disabled: true,
			maxlength: 10,
			id: 'birth-date',
		}),
	};

	return inputs;
};

export const resetSuccessMessages = (inputs: Inputs) => {
	Object.keys(inputs).forEach((key) => {
		const input = inputs[key as keyof typeof inputs];
		input.setSuccess('');
	});
};

export const handleInputsDisabled = (inputs: Inputs, disable: boolean) => {
	Object.keys(inputs).forEach((key) => {
		inputs[key as keyof typeof inputs].setDisabled(disable);
	});
};

export const validatePhoneNumber = async (handler: InputHandler<string>, userInfo: UserInfo | null) => {
	const phone = handler.value;
	const unmaskedNumber = phone.replace(/\D/g, '') || '';
	if (unmaskedNumber === userInfo?.mobileNum) {
		return true;
	}
	const phoneAvailability = await DineRewardsManager.CheckPhoneNumberAvailability(phone);
	const isPhoneAvailable = phoneAvailability.isAvailable;
	if (!phoneAvailability.isValid) {
		handler.setError(Strings.errors.general.incorrectFormatting);
		scrollIntoInput(handler);
		return false;
	}
	if (isPhoneAvailable) {
		handler.setSuccess(Strings.success.greatLetsContinue);
		return true;
	} else {
		handler.setError(Strings.errors.phoneNumber.alreadyInUse);
		scrollIntoInput(handler);
		return false;
	}
};

export const validateEmail = async (
	handler: InputHandler<string>,
	userInfo: UserInfo | null,
	gdprExtensions: string[] | null
) => {
	const email = handler.value;
	if (email === userInfo?.email) {
		return true;
	}

	const emailExtension = email.split('@')[1];
	if (!emailExtension) {
		return returnInvalidFormatting(handler);
	}
	const extension = emailExtension.split('.')[0];
	if (gdprExtensions?.includes('.' + extension)) {
		return returnInvalidFormatting(handler);
	}

	const isEmailAvailable = await DineRewardsManager.CheckEmailAvailability(email);
	if (!isEmailAvailable) {
		handler.setError(Strings.errors.email.alreadyInUse);
		scrollIntoInput(handler);
		return false;
	} else {
		handler.setSuccess(Strings.success.greatLetsContinue);
		return true;
	}
};

export const returnInvalidFormatting = (handler: InputHandler<string>) => {
	handler.setError(Strings.errors.general.incorrectFormatting);
	scrollIntoInput(handler);
	return false;
};

export const scrollIntoInput = (handler: InputHandler<string>) => {
	const input = document.getElementById(handler.id + '-input');
	input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export const hasFormErrors = (formInputs: Inputs) => {
	return Object.keys(formInputs).some((key) => {
		const input = formInputs[key as keyof typeof formInputs];
		if (input.error) {
			scrollIntoInput(input);
			return true;
		}
		return false;
	});
};

export const validateName = async (
	firstNameHandler: InputHandler<string>,
	lastNameHandler: InputHandler<string>,
	userInfo: UserInfo | null
) => {
	const firstName = firstNameHandler.value;
	const lastName = lastNameHandler.value;

	if (firstName === userInfo?.given_name && lastName === userInfo?.family_name) {
		return true;
	}

	const nameValidation = await DineRewardsManager.GetProfanityCheck(firstName, lastName);
	const isNameValid = nameValidation?.first === true && nameValidation?.last === true;
	if (isNameValid) {
		lastNameHandler.setSuccess(Strings.success.greatLetsContinue);
		return true;
	}
	if (!nameValidation?.first) {
		firstNameHandler.setError(Strings.errors.name.invalid);
	}
	if (!nameValidation?.last) {
		lastNameHandler.setError(Strings.errors.name.invalid);
	}
	return false;
};

export const getSubmitBody = (
	formInputs: Inputs,
	printedCardNumber: string,
	favoriteLocation: string
): AccountRegisterInfo => {
	const password = formInputs.newPassword.value.length === 0 ? null : formInputs.newPassword.value;
	const body = {
		email: formInputs.email.value,
		password: password,
		firstName: formInputs.firstName.value,
		lastName: formInputs.lastName.value,
		phone: formInputs.phoneNumber.value,
		postalCode: formInputs.zipCode.value,
		printedCardNumber: printedCardNumber,
		favoriteLocation: setResponseFromFavoriteLocation(favoriteLocation),
	};
	if (formInputs.birthDate.value.length === 0) {
		return body;
	}
	const date = stringToDate(formInputs.birthDate.value);
	return { dateOfBirth: date, ...body };
};

export const setResponseFromFavoriteLocation = (location: string) => {
	switch (location) {
		case FavoriteLocations.Outback:
			return 'OBS';
		case FavoriteLocations.Carrabbas:
			return 'CIG';
		case FavoriteLocations.Bonefish:
			return 'BFG';
		case FavoriteLocations.Fleming:
			return 'FLM';
		default:
			return FavoriteLocations.Default;
	}
};

export const setFavoriteLocationFromResponse = (location: string) => {
	switch (location) {
		case 'Outback Steakhouse':
		case 'Outback':
		case 'OBS':
			return FavoriteLocations.Outback;
		case `Carrabbas Italian Grill`:
		case 'Carrabbas':
		case 'CIG':
			return FavoriteLocations.Carrabbas;
		case 'Bonefish Grill':
		case 'Bonefish':
		case 'BFG':
			return FavoriteLocations.Bonefish;
		case 'Flemings':
		case 'FLM':
			return FavoriteLocations.Fleming;
		default:
			return FavoriteLocations.Default;
	}
};

export const setCookie = (name: string, value: string, days = 1) => {
	const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
	document.cookie = `${name}=${value}; expires=${expires}; path=/`;
};

export const getCookie = (name: string) => {
	const cookies = document.cookie.split('; ');
	const cookie = cookies.find((row) => row.startsWith(`${name}=`));
	return cookie ? cookie.split('=')[1] : null;
};

export const removeCookie = (name: string) => {
	document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
};